export function isDateTimeAfterToday(dateTime) {
  const now = new Date();
  const min = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    now.getMinutes() + 1,
    now.getSeconds(),
  );
  return dateTime > min;
}

export function generateDateTimeArray() {
  const startTime = new Date("January 1, 2000 00:00:00");
  const endTime = new Date("January 1, 2000 23:45:00");
  const timeArray = [];

  while (startTime <= endTime) {
    const hours = startTime.getHours();
    const minutes = startTime.getMinutes();
    const timeString = `${hours.toString().padStart(2, "0")}h${minutes
      .toString()
      .padStart(2, "0")}`;
    timeArray.push(timeString);
    startTime.setTime(startTime.getTime() + 15 * 60000);
  }
  timeArray.push("23h59");

  return timeArray;
}

export function updateDateTime(day, time) {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    parseInt(time.split("h")[0]),
    parseInt(time.split("h")[1]),
  );
}

export function getNextTimeSlot(timeArray) {
  const now = new Date();
  const index = Math.min(
    Math.round((now.getHours() * 60 + now.getMinutes()) / 15) + 1,
    95,
  );
  return timeArray[index];
}

/**
 * Get relative time string (e.g., "10 minutes ago", "12 days ago", "3 months ago")
 */
function getRelativeTime(date) {
  if (!date) return "";

  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? "" : "s"} ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} year${diffInYears === 1 ? "" : "s"} ago`;
}

/**
 * Allows to format a datetime within the given optional format.
 * @example
 * With a date = "2025-04-15T13:15:00.000Z"
 * default # 4/15/2025, 3:15:00 PM
 * short # 4/15/25, 3:15 PM
 * shortTimeMediumDate # Apr 15, 3:15 PM
 * shortTimeLongDate # Apr 15, 25, 3:15 PM
 * medium # Apr 15, 2025, 3:15:00 PM
 * long # April 15, 2025 at 3:15:00 PM GMT+2
 * full # Tuesday, April 15, 2025 at 3:15:00 PM Central European Summer Time
 * shortDate # 4/15/25
 * mediumDate # Apr 15, 2025
 * longDate # April 15, 2025
 * fullDate # Tuesday, April 15, 2025
 * shortTime # 3:15 PM
 * mediumTime # 3:15:00 PM
 * longTime # 3:15:00 PM GMT+2
 * fullTime # 3:15:00 PM Central European Summer Time
 * relative # 10 minutes ago, 12 days ago, 3 months ago
 * tooltip # Aug 26, 2025 at 3:04pm (for hover tooltips)
 */

export const formatDateTime = (date, format) => {
  let options = {};
  switch (format) {
    case "short":
      options = {
        day: "numeric",
        month: "numeric",
        year: "2-digit",
        hour: "numeric",
        minute: "numeric",
      };
      break;
    case "shortTimeMediumDate":
      options = {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "numeric",
      };
      break;
    case "shortTimeLongDate":
      options = {
        day: "numeric",
        month: "short",
        year: "2-digit",
        hour: "numeric",
        minute: "numeric",
      };
      break;
    case "medium":
      options = {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
      };
      break;
    case "long":
      options = {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        timeZoneName: "short",
      };
      break;
    case "full":
      options = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        timeZoneName: "long",
      };
      break;
    case "shortDate":
      options = {
        day: "numeric",
        month: "numeric",
        year: "2-digit",
      };
      break;
    case "mediumDate":
      options = {
        day: "numeric",
        month: "short",
        year: "numeric",
      };
      break;

    case "longDate":
      options = {
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      break;

    case "fullDate":
      options = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      break;

    case "shortTime":
      options = {
        hour: "numeric",
        minute: "numeric",
      };
      break;

    case "mediumTime":
      options = {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
      };
      break;

    case "longTime":
      options = {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        timeZoneName: "short",
      };
      break;

    case "fullTime":
      options = {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        timeZoneName: "long",
      };
      break;

    case "relative":
      return getRelativeTime(date);

    case "tooltip":
      options = {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      };
      break;

    default:
      break;
  }
  return date?.toLocaleString("en-US", options);
};
